import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { currentIndustryAtom } from '@/store/industryStore';
import { Industry } from '@/types/types';
import styles from './IndustryStickyNav.module.css';
import { isStickyNavAtom } from '@/store/globalStore';
import IndustryTab from './IndustryTab';
import { FaArrowLeft } from 'react-icons/fa6';

interface IndustryNavProps {
  industries: Industry[];
  sectionInView?: boolean;
  industryNavInView?: boolean;
}

export default function IndustryStickyNav({
  industries,
  sectionInView,
  industryNavInView,
}: IndustryNavProps) {
  const [currentIndustry, setCurrentIndustry] = useAtom(currentIndustryAtom);
  const [isStickyNav] = useAtom(isStickyNavAtom);
  const stickyRef = useRef(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) return;

    const currentIndex = industries.findIndex(
      (industry) => industry.title === currentIndustry?.title
    );

    if (currentIndex !== -1 && tabRefs.current[currentIndex]) {
      requestAnimationFrame(() => {
        tabRefs.current[currentIndex]?.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      });
    }
  }, [currentIndustry, industries, industryNavInView, isStickyNav]);

  useEffect(() => {
    if (!industryNavInView && isStickyNav) {
      hasMounted.current = true;
    }
  }, [industryNavInView, isStickyNav]);

  return (
    <div ref={stickyRef} className={`container`}>
      {!industryNavInView && isStickyNav && (
        <div
          className={`fixed left-0 right-0 top-16 z-50 mx-auto w-full max-w-[1535px] translate-y-1 transform bg-charcoal pl-6 lg:pl-10 ${sectionInView ? 'opacity-100' : 'opacity-0'}`}
        >
          <div
            className={`flex items-center justify-start gap-4 overflow-x-scroll py-4 ${styles.scrollHide}`}
          >
            <button
              onClick={() => setCurrentIndustry(null)}
              className='inline-flex items-center justify-center gap-2 rounded bg-[#51504D] px-4 py-2'
            >
              <FaArrowLeft
                size={14}
                className='!h-[35px] min-w-[35px] max-w-[35px] rounded-full bg-white object-contain p-2 text-[#51504D]'
              />
              <p className='whitespace-nowrap text-sm text-white'>
                All industries
              </p>
            </button>

            {industries.map((industry, index) => (
              <button
                key={index}
                ref={(el) => (tabRefs.current[index] = el)}
                onClick={() => setCurrentIndustry(industry)}
              >
                <IndustryTab
                  title={industry.title}
                  industryIcon={industry.industryIcon}
                  industryColor={industry.industryColor}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
